/**
 * Quote store — the Firestore seam for the quote module (impure).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  ⚠ THE DEPLOYED QUOTE CONTRACT (verified 2026-06-16 — supersedes the old
 *  "team-gated, no rules change" assumption): `quotes/` is governed by a
 *  STRICT deployed `firestore.rules` block (CREATE = draft-only, leadId
 *  required, forbids quoteNo/validUntil/total/payment fields; UPDATE = a
 *  tri-mode state gate with CF-only-field immutability) AND a live trigger
 *  suite (`onQuoteCreate_stampDefaults` stamps quoteNo/validUntil/partyId,
 *  `onQuoteWrite_recalcTotals` computes `total` from legacy `items[]`,
 *  `onQuoteUpdate_guardStateMachine` reverts illegal transitions,
 *  `onQuoteAcceptManual_promoteLeadToClient` fires on sent→accepted). So:
 *   • `createQuote` writes a BARE, rule-valid draft (`quoteModel.quoteCreate-
 *     Body`); the server stamps quoteNo/validUntil/total.
 *   • `saveQuote` (draft edits) writes only rule-allowed draft fields
 *     (`quoteModel.quoteDraftUpdateBody`), never the CF-only fields.
 *   • LIFECYCLE is draft → sent → accepted (the rule + the state machine only
 *     allow these). `acceptQuote` requires the quote be `sent` first.
 *
 *  THE ONE SPINE (no parallel money path): `acceptQuote` does NOT write any
 *  gate itself — it routes the accepted total through `setProjectAgreedPrice`
 *  (the EXACT plumbing the Set-price screen + Payment-tab editor use), which
 *  seeds `gateAmounts` + appends `feeHistory` via the canonical resolver, AND
 *  performs the rule-conformant sent→accepted (manual-mode) status write so
 *  the deployed promote-lead-to-client trigger fires. The accepted grand total
 *  IS the agreed price.
 *
 *  SEND: `sendQuote` simply calls the deployed `sendQuoteViaAiSensy` callable
 *  (admin SDK) which flips status draft→sent + stamps sentAt/sentVia/total/
 *  payment-link server-side — we do NOT pre-write those client-side (that hit
 *  the CF-only-field immutability + the state machine). EMAIL works now
 *  (Brevo); WHATSAPP is gated behind the owner's BSP template approval (the
 *  callable self-skips an unapproved template; quoteSend.ts also gates the UI).
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { setProjectAgreedPrice } from './recordPaymentStore';
import {
  acceptedAgreedPrice,
  quoteCreateBody,
  quoteDraftUpdateBody,
  quoteFromDoc,
  quoteTotals,
  resolveQuoteContactId,
  reviseQuoteDraft,
  sortQuotes,
  type Quote,
} from './quoteModel';
import type { SendChannel, SendQuoteResp, FirmPayment } from './quoteSend';
import { firmPaymentFromStatic, type FirmId } from './firms';
import { primaryPhone, primaryEmail } from './contactModel';
import { advanceLeadOnQuoteSent, type Lead } from './leads';
import { claimLeadConversionTx, getLead, updateLead } from './leadStore';
import {
  convertLeadPersist,
  findConvertedProjectForLead,
  type ConvertClientTarget,
} from './projectStore';
import {
  acceptLeadOriginQuote,
  freshQuoteRefusal,
  roleRefusal,
  type AcceptConvertDeps,
  type AcceptDriveResult,
  type AcceptRoleGate,
  type FreshQuoteGate,
} from './quoteAccept';

/** A picklist acceptance reason — must match the deployed UPDATE rule's set. */
export type AcceptanceReason =
  | 'pro_bono_family'
  | 'pro_bono_referral'
  | 'pro_bono_case_study'
  | 'partial_waiver'
  | 'other';

/* ════════════════════ Read ════════════════════ */

/** Live realtime list of quotes (newest-updated first). Team-gated read. */
export function listenQuotes(
  cb: (rows: Quote[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'quotes')),
    (snap) => {
      const rows: Quote[] = [];
      for (const dd of snap.docs) {
        const data = dd.data();
        // Skip legacy quote docs that don't carry the redesigned `lines`
        // shape (the old Phase-1 docs use `items`); the redesign list shows
        // only redesigned quotes. A legacy doc has no `lines` array.
        if (!Array.isArray(data.lines)) continue;
        rows.push(quoteFromDoc(dd.id, data));
      }
      cb(sortQuotes(rows));
    },
    (e) => onErr?.(e),
  );
}

/** One quote by id (or null). */
export async function loadQuote(id: string): Promise<Quote | null> {
  const snap = await getDoc(doc(db, 'quotes', id));
  if (!snap.exists()) return null;
  const data = snap.data();
  if (!Array.isArray(data.lines)) return null;
  return quoteFromDoc(snap.id, data);
}

/* ════════════════════ Contact spine — resolve the quote's clientId (AD-039) ════════════════════
 *
 *  IMPURE companion to the PURE `resolveQuoteContactId`. When a quote has no
 *  `clientId` yet, this resolves one via the SAME precedence the recipient
 *  resolver uses (project.clientId → lead.contactId) with at most one getDoc
 *  per source, short-circuiting as soon as a clientId is found. Returns the
 *  resolved `clients/{cid}` id, or null when nothing resolves. No money path —
 *  purely the identity link; the caller stamps `clientId`.
 */
export async function resolveQuoteClientId(quote: Quote): Promise<string | null> {
  // 1) already stamped — strongest, no read needed.
  if (quote.clientId) return quote.clientId;

  // 2) project.clientId (source-of-truth once the engagement exists).
  let projectClientId: string | null = null;
  if (quote.projectId) {
    try {
      const projSnap = await getDoc(doc(db, 'projects', quote.projectId));
      if (projSnap.exists()) {
        const p = projSnap.data() as Record<string, unknown>;
        projectClientId =
          (typeof p.clientId === 'string' && p.clientId) ||
          (typeof p.clientNo === 'string' && p.clientNo) ||
          null;
      }
    } catch {
      /* ignore — degrade to the lead path / null */
    }
  }
  if (projectClientId) {
    return resolveQuoteContactId(quote, { projectClientId });
  }

  // 3) lead.contactId (the front-door spine link).
  let leadContactId: string | null = null;
  if (quote.leadId) {
    try {
      const leadSnap = await getDoc(doc(db, 'leads', quote.leadId));
      if (leadSnap.exists()) {
        const l = leadSnap.data() as Record<string, unknown>;
        // WA/website flat docs carry contactId at the top level; snapshot-shaped
        // docs carry it inside notes_internal — read the flat field, then fall
        // back to the snapshot when present.
        if (typeof l.contactId === 'string' && l.contactId) {
          leadContactId = l.contactId;
        } else if (typeof l.notes_internal === 'string') {
          try {
            const snap = JSON.parse(l.notes_internal) as { contactId?: unknown };
            if (typeof snap?.contactId === 'string' && snap.contactId) {
              leadContactId = snap.contactId;
            }
          } catch {
            /* not a snapshot note — ignore */
          }
        }
      }
    } catch {
      /* ignore — degrade to null */
    }
  }
  return resolveQuoteContactId(quote, { leadContactId });
}

/* ════════════════════ Write ════════════════════ */

/**
 * CREATE a new quote — a BARE, rule-valid draft. The doc id is the quote's
 * `id` (the UI mints `QT-<ts>`). Writes ONLY the fields the deployed CREATE
 * rule permits (`quoteModel.quoteCreateBody` — status:'draft', non-empty
 * leadId, NO quoteNo/validUntil/total/payment fields + the legacy items[]
 * mirror); the `stampDefaults`/`recalcTotals` triggers fill the rest server-
 * side. `createdBy` is the actor (the audit/stamp triggers read it).
 *
 * The live rule (2026-07-03 deploy) accepts EITHER a non-empty leadId OR a
 * non-empty projectId, so a project-origin quote (no lead) saves normally.
 */
/**
 * BREAK-3 (linkage audit, 2026-07-03) — the lead record's price-drift guard
 * needs the open SENT quote's FIGURE, not just its id: when a hand-set
 * agreed price differs from the open quote's total, accepting the quote will
 * silently overwrite the hand-set figure (priceLeadFromQuote). One-shot
 * equality query (leadId + status, single-field indexes); the total prefers
 * the CF-stamped `total` and falls back to the pure quoteTotals off the
 * lines. Null when the lead has no open sent quote.
 */
export async function findOpenSentQuoteForLead(
  leadId: string,
): Promise<{ id: string; total: number; currency: 'INR' | 'USD' } | null> {
  const snap = await getDocs(
    query(
      collection(db, 'quotes'),
      where('leadId', '==', leadId),
      where('status', '==', 'sent'),
    ),
  );
  const d = snap.docs[0];
  if (!d) return null;
  const raw = d.data() as Record<string, unknown>;
  const currency: 'INR' | 'USD' = raw.currency === 'USD' ? 'USD' : 'INR';
  const stamped = Number(raw.total);
  if (Number.isFinite(stamped) && stamped > 0) {
    return { id: d.id, total: Math.round(stamped), currency };
  }
  const lines = Array.isArray(raw.lines) ? (raw.lines as Quote['lines']) : [];
  const t = quoteTotals(lines, currency, raw.gst === true, null);
  return { id: d.id, total: Math.round(t.grandTotal), currency };
}

export async function createQuote(quote: Quote, actorName: string): Promise<void> {
  // Contact spine (AD-039): resolve + stamp the quote's `clientId` when null so
  // the quote carries its single contact anchor from creation. `clientId` is a
  // rules-permitted create field (not in the forbidden list). No money path —
  // the identity link only.
  const stamped: Quote = quote.clientId
    ? quote
    : { ...quote, clientId: await resolveQuoteClientId(quote) };
  await setDoc(doc(db, 'quotes', stamped.id), {
    ...quoteCreateBody(stamped),
    createdBy: actorName,
    updatedBy: actorName,
    _createdAt: serverTimestamp(),
  });
}

/**
 * Persist a DRAFT EDIT (line/discount/currency changes while still a draft).
 * Conforms to UPDATE-rule mode (1) (status stays draft) + CF-only-field
 * immutability — writes only `quoteModel.quoteDraftUpdateBody` (never quoteNo/
 * validUntil/subtotal/total/paymentLinkUrl/partyId; numeric `discount`). The
 * `recalcTotals` trigger re-derives `total`. Merge-write so server stamps
 * survive.
 */
export async function saveQuote(quote: Quote, actorName: string): Promise<void> {
  // Contact spine (AD-039): stamp `clientId` on a draft edit when it is still
  // null and resolvable (lead/project gained a contact since create). `clientId`
  // is rules-allowed on update and not in any forbidden-field list. Identity
  // link only — never a money write.
  const clientId = quote.clientId ?? (await resolveQuoteClientId(quote));
  await setDoc(
    doc(db, 'quotes', quote.id),
    {
      ...quoteDraftUpdateBody(quote),
      ...(clientId ? { clientId } : {}),
      updatedBy: actorName,
      _updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

/**
 * ACCEPT a quote → its grand total becomes the project's AGREED PRICE.
 *
 * RULE-CONFORMANT LIFECYCLE: the deployed UPDATE rule + state machine only
 * allow `sent → accepted`. So the quote MUST be `sent` first (the UI gates
 * Accept behind Send). This writes the MANUAL-mode acceptance the rule permits
 * (`status:'accepted'` + an `acceptanceReason` from the locked picklist) — the
 * deployed `onQuoteAcceptManual_promoteLeadToClient` trigger reacts to it
 * (and short-circuits when the write already carries a `clientId`).
 *
 * TWO ORIGINS, ONE MONEY SPINE:
 *  · PROJECT-ORIGIN (`projectId` set) — unchanged: routes the accepted total
 *    through `setProjectAgreedPrice` (no gate math here) so it seeds
 *    `gateAmounts` + `feeHistory` via the one canonical resolver, then flips
 *    sent→accepted. `prior` drives the change-history was→now.
 *  · LEAD-ORIGIN (no `projectId` yet) — 2026-07-03 streamline Phase 1d:
 *    acceptance DRIVES the conversion (the old circular dead-end refused
 *    without a projectId that only conversion could mint). Orchestrated in
 *    `quoteAccept.acceptLeadOriginQuote`: price the lead from the quote →
 *    6-must-haves gate → convert via the EXISTING `convertLeadPersist`
 *    promote path (the accepting quote is excluded from the open-quote
 *    guard) → seed the agreed price via `setProjectAgreedPrice` from the
 *    quote total → stamp `projectId`+`clientId` back on the quote in the
 *    same sent→accepted manual-mode write. Re-running a partially-failed
 *    accept is safe (an already-converted lead short-circuits to its
 *    existing project; the price seed no-ops on the same value).
 *
 * VEX GATE F1 / OPTION A (2026-07-03, owner-approved): BOTH origins validate
 * the fail-fast preconditions BEFORE any write — caller session is
 * Sales/Admin (mirrors the new rules gate (3)'s actor set, functions branch
 * `feat/quote-manual-accept-rule` @ 7e9aa16), and a FRESH read of the quote
 * doc shows `status=='sent'`. The final stamp carries the accept SHAPE the
 * new gate requires (post-write `acceptanceMode:'manual'`, non-empty
 * `projectId`, non-empty `updatedBy`, picklist reason; `acceptedAt` stays
 * CF-stamped). A rules denial at the stamp (rule drift or a missing team
 * claim — the accept rule itself is live) is caught and surfaced honestly —
 * never a silent half-failure.
 */
export async function acceptQuote(
  quote: Quote,
  prior: { currency: 'INR' | 'USD' | null; value: number | null },
  actorName: string,
  acceptanceReason: AcceptanceReason = 'other',
  role?: AcceptRoleGate,
): Promise<AcceptDriveResult> {
  if (!quote.projectId) {
    // LEAD-ORIGIN — acceptance drives conversion (see module header).
    return acceptLeadOriginQuote(
      quote,
      prior,
      actorName,
      acceptanceReason,
      firestoreAcceptDeps(),
      new Date().toISOString(),
      role,
    );
  }
  // F1 preconditions — role first (mirrors the rules actor set), then status.
  const denied = roleRefusal(role);
  if (denied) return denied;
  if (quote.status !== 'sent') {
    // The rule/state-machine reject draft→accepted. Send it first.
    return { ok: false, error: 'not_sent' };
  }
  const refusal = freshQuoteRefusal(await getQuoteFreshFs(quote.id));
  if (refusal) return refusal;
  // Contact spine (AD-039): the project exists on accept — ensure the quote
  // carries its `clients/{cid}` link (project.clientId is source-of-truth) so an
  // accepted quote always resolves to a contact. Identity link only.
  const clientId = quote.clientId ?? (await resolveQuoteClientId(quote));
  const agreed = acceptedAgreedPrice(quote);
  await setProjectAgreedPrice(
    quote.projectId,
    {
      tier: agreed.tier,
      currency: agreed.currency,
      value: agreed.value,
      prior,
      gst: agreed.gst,
      note: `Accepted quote ${quote.id}`,
    },
    actorName,
  );
  // The accept SHAPE the new gate (3) requires: sent → accepted, manual mode,
  // picklist reason, NON-EMPTY projectId (explicit — not just already-on-doc)
  // and updatedBy. Defensive catch: an un-deployed/drifted rule surfaces
  // honestly; the seeded price is idempotent on retry.
  try {
    await updateDoc(doc(db, 'quotes', quote.id), {
      status: 'accepted',
      acceptanceMode: 'manual',
      acceptanceReason,
      projectId: quote.projectId,
      ...(clientId ? { clientId } : {}),
      updatedBy: actorName,
      _updatedAt: serverTimestamp(),
    });
  } catch (e) {
    return {
      ok: false,
      error: 'stamp_denied',
      projectId: quote.projectId,
      message:
        `The agreed price was seeded on ${quote.projectId}, but the quote ` +
        `could not be marked accepted — security rules blocked the write ` +
        `(${(e as Error).message}). Tell Larry (likely a missing team claim ` +
        `or rule drift); retrying Accept later finishes the job.`,
    };
  }
  return { ok: true, projectId: quote.projectId };
}

/**
 * F1 — FRESH-read the quote doc's rules-precondition fields (status +
 * acceptanceMode). Never trust the UI's Quote copy for the accept gate: the
 * `recalcTotals` CF may have re-asserted acceptanceMode server-side.
 */
async function getQuoteFreshFs(quoteId: string): Promise<FreshQuoteGate | null> {
  const snap = await getDoc(doc(db, 'quotes', quoteId));
  if (!snap.exists()) return null;
  const d = snap.data() as Record<string, unknown>;
  return {
    status: typeof d.status === 'string' ? d.status : '',
    acceptanceMode:
      typeof d.acceptanceMode === 'string' ? d.acceptanceMode : null,
  };
}

/**
 * The PRODUCTION seams for the accept→convert orchestrator (quoteAccept.ts).
 * Every write goes through the EXISTING plumbing — `claimLeadConversionTx`
 * (the durable convert reservation), `convertLeadPersist` (promote),
 * `updateLead` (whitelisted lead persist), `setProjectAgreedPrice` (the one
 * money spine; called, never modified) — no second path.
 */
function firestoreAcceptDeps(): AcceptConvertDeps {
  return {
    getLead: (id) => getLead(id),

    getQuoteFresh: (id) => getQuoteFreshFs(id),

    // F2 — the atomic conversion claim (a Firestore transaction on the lead).
    claimConversion: (leadId, claimId, actor) =>
      claimLeadConversionTx(leadId, claimId, actor),

    // F2 — the sourceLeadId back-ref query (crash-recovery resume).
    findConvertedProject: (leadId) => findConvertedProjectForLead(leadId),

    // Mirrors the manual door's contact-spine resolution (Leads.tsx doConvert):
    // an already-linked contact converts onto the EXISTING client (no new
    // C##### is minted); otherwise a fresh client is created. (The manual
    // door's fuzzy re-match sweep needs the full contact pool and stays UI-side.)
    resolveConvertTarget: async (lead: Lead): Promise<ConvertClientTarget> => {
      if (!lead.contactId) return { mode: 'new' };
      try {
        const snap = await getDoc(doc(db, 'clients', lead.contactId));
        if (!snap.exists()) return { mode: 'new' };
        const c = snap.data() as Record<string, unknown>;
        if (c.trashed === true) return { mode: 'new' };
        return {
          mode: 'existing',
          clientId: lead.contactId,
          clientNo:
            (typeof c.clientNo === 'string' && c.clientNo) || lead.contactId,
          ...(typeof c.name === 'string' && c.name ? { clientName: c.name } : {}),
        };
      } catch {
        return { mode: 'new' };
      }
    },

    // THE existing promote path. The accepting quote id is threaded to the
    // open-sent-quote guard so the quote never blocks its own conversion.
    convertLead: async (lead, actor, nowISO, target, excludeQuoteId) => {
      const res = await convertLeadPersist(lead, actor, nowISO, target, null, '', {
        excludeQuoteId,
      });
      return res;
    },

    persistLead: (lead, actor, what) => updateLead(lead, actor, what),

    setAgreedPrice: (projectId, input, actor) =>
      setProjectAgreedPrice(projectId, input, actor),

    // Rule mode (3): sent → accepted, manual mode, reason from the locked
    // picklist — PLUS the projectId/clientId stamp (neither is a CF-only
    // field, so the same conformant write carries them). clientId present →
    // the legacy promote CF short-circuits (no duplicate client).
    stampQuoteAccepted: async (q, stamp, actor) => {
      await updateDoc(doc(db, 'quotes', q.id), {
        status: 'accepted',
        acceptanceMode: 'manual',
        acceptanceReason: stamp.acceptanceReason,
        projectId: stamp.projectId,
        ...(stamp.clientId ? { clientId: stamp.clientId } : {}),
        updatedBy: actor,
        _updatedAt: serverTimestamp(),
      });
    },
  };
}

/**
 * SEND a quote (draft → sent). EMAIL works now (Brevo); WHATSAPP is gated
 * behind BSP template approval (the callable self-skips an unapproved template,
 * and the UI gates the WA channel via `quoteSend.sendChannels`).
 *
 * We do NOT pre-write status/total/quoteNo client-side — that violated the
 * CF-only-field immutability + the deployed state machine. The deployed
 * `sendQuoteViaAiSensy` callable (admin SDK) flips status draft→sent and stamps
 * sentAt/sentVia/total/payment-link itself. We just call it.
 *
 * ⚠ The callable is AUTH-GATED (it requires a `teamUserId` claim AND
 * role∈{admin,sales} — NOT merely the owner email) and runs in PROD — from the
 * preview it calls prod functions. If the signed-in session lacks the team
 * claim the callable returns permission-denied (the quote stays a draft, which
 * is correct — nothing was sent). Flagged to Varun.
 */
export async function sendQuote(
  quote: Quote,
  channels: SendChannel[],
  _contact: { email: string | null; phone: string | null },
  actorName: string,
): Promise<SendQuoteResp> {
  try {
    // 2026-07-03 cleanup (Mack): the callable's canonical export is now
    // 'sendQuote' (AiSensy is GONE — the send rides the Meta Cloud API);
    // the old alias name is retired with this rename.
    const fn = httpsCallable<{ quoteId: string; channels: string[] }, SendQuoteResp>(
      functions,
      'sendQuote',
    );
    const res = await fn({ quoteId: quote.id, channels });
    // ── EVENT-DRIVEN STAGE MOVE (2026-07-03, streamline Phase 1b) ──
    // A REAL send advances the quote's lead to `quoted` through the ONE
    // state machine (pure `advanceLeadOnQuoteSent` — legal transitionLead
    // hops only) + stamps the lead's sentAt audit. Best-effort: the quote
    // WAS sent, so a lead-update failure never fails the send result.
    if (res.data?.ok && quote.leadId) {
      await advanceQuoteLead(quote, channels, actorName, res.data);
    }
    return res.data;
  } catch (e) {
    // The callable can 404 / permission-fail in the preview (prod functions +
    // team claim). Surface the failure; the doc is untouched (still a draft).
    return { ok: false, error: e instanceof Error ? e.message : 'send_failed' };
  }
}

/**
 * Best-effort lead advance after a successful quote send (never throws).
 * `already_sent` re-sends only HEAL a drifted lead (state advance, no fresh
 * sentAt restamp implied — the helper stamps `sentAt: now` which for a heal
 * is the moment the drift was closed).
 */
async function advanceQuoteLead(
  quote: Quote,
  channels: SendChannel[],
  actorName: string,
  resp: SendQuoteResp,
): Promise<void> {
  try {
    const lead = await getLead(quote.leadId as string);
    if (!lead) return;
    // A confirmed re-send of an already-sent quote: nothing to do when the
    // lead already reads Quoted (avoid a meaningless sentAt restamp).
    if (resp.skipped === 'already_sent' && lead.state === 'quoted') return;
    const advanced = advanceLeadOnQuoteSent(
      lead,
      new Date().toISOString(),
      channels,
    );
    if (advanced === lead) return; // terminal state — never regress
    await updateLead(
      advanced,
      actorName,
      `Quote ${quote.id} sent → lead Quoted (auto)`,
    );
  } catch {
    /* non-fatal — the send succeeded; the lead can be corrected on the board */
  }
}

/**
 * Impure — the firm's static-payment slots for the SEND preview, overlaid with
 * the Firestore `firms/{firmId}` doc (so a `qrUrl` and any owner corrections
 * appear). Mirrors the CF `loadFirmPayment`: Firestore value wins where present,
 * static FIRMS bank details fill the rest. Never throws — degrades to static.
 */
export async function loadFirmPayment(firmId: FirmId): Promise<FirmPayment> {
  const base = firmPaymentFromStatic(firmId);
  try {
    const snap = await getDoc(doc(db, 'firms', firmId));
    const f = (snap.exists() ? snap.data() : {}) as Record<string, unknown>;
    const s = (k: string): string | null => (typeof f[k] === 'string' && (f[k] as string).trim() ? (f[k] as string) : null);
    return {
      upiId: s('upiId') ?? base.upiId,
      bankAccName: s('bankAccName') ?? base.bankAccName,
      bankAccNo: s('bankAccNo') ?? base.bankAccNo,
      bankIfsc: s('bankIfsc') ?? base.bankIfsc,
      bankName: s('bankName') ?? base.bankName,
      qrUrl: s('qrUrl') ?? base.qrUrl,
      swift: s('swift') ?? base.swift,
      bsrAdCode: s('bsrAdCode') ?? base.bsrAdCode,
    };
  } catch {
    return base;
  }
}

/**
 * Impure — resolve the quote's recipient (name/phone/email) the SAME way the CF
 * does (Bug A), so the Send screen enables the channels for a PROJECT-origin
 * quote (no lead). Precedence: explicit stamped lead* → Contact via clientId →
 * project's client → lead. `leadPhone`/`leadEmail`/`leadName` may be on the doc
 * after a previous send. Returns nulls when nothing resolves (UI stays honest).
 */
export async function resolveQuoteRecipientContact(quote: Quote): Promise<{
  name: string | null;
  phone: string | null;
  email: string | null;
}> {
  const out: { name: string | null; phone: string | null; email: string | null } = {
    name: quote.clientName?.trim() || null,
    phone: null,
    email: null,
  };
  // Precedence 1 — the delivery-party snapshot on the quote itself (stamped at
  // compose-from-lead since 2026-07-03, and refreshed by the send CF).
  if (quote.leadPhone) out.phone = quote.leadPhone;
  if (quote.leadEmail) out.email = quote.leadEmail;

  const fromContact = async (cid: string) => {
    try {
      const snap = await getDoc(doc(db, 'clients', cid));
      if (!snap.exists()) return;
      const c = snap.data() as Record<string, unknown>;
      if (!out.name && typeof c.name === 'string') out.name = c.name;
      if (!out.phone) {
        const p = primaryPhone(c as { phones?: { number: string; label?: string }[]; phone?: string });
        if (p) out.phone = p;
      }
      if (!out.email) {
        const e = primaryEmail(c as { emails?: { address: string; label?: string }[]; email?: string });
        if (e) out.email = e;
      }
    } catch {
      /* ignore — UI degrades to no-destination */
    }
  };

  if ((!out.phone || !out.email) && quote.clientId) await fromContact(quote.clientId);
  if ((!out.phone || !out.email) && quote.projectId) {
    try {
      const projSnap = await getDoc(doc(db, 'projects', quote.projectId));
      if (projSnap.exists()) {
        const p = projSnap.data() as Record<string, unknown>;
        const cid = (typeof p.clientId === 'string' && p.clientId) || (typeof p.clientNo === 'string' && p.clientNo) || null;
        if (cid) await fromContact(cid);
      }
    } catch { /* ignore */ }
  }
  if ((!out.phone || !out.email) && quote.leadId) {
    try {
      const leadSnap = await getDoc(doc(db, 'leads', quote.leadId));
      if (leadSnap.exists()) {
        const l = leadSnap.data() as Record<string, unknown>;
        if (!out.name && typeof l.name === 'string') out.name = l.name;
        if (!out.phone && typeof l.phone === 'string' && l.phone) out.phone = l.phone;
        if (!out.email && typeof l.email === 'string' && l.email) out.email = l.email;
      }
    } catch { /* ignore */ }
  }
  return out;
}

/**
 * HARD-DELETE a quote (owner ask 2026-06-16 — clear useless drafts/records).
 *
 * ⚠ RULE-GATED: the live `quotes/` delete rule (2026-07-03 deploy) permits:
 *   • admin OR sales may delete a DRAFT quote
 *   • admin may additionally delete a non-accepted quote (sent/rejected/
 *     superseded/expired)
 *   • an ACCEPTED quote is NEVER hard-deletable (use supersede/expire).
 * The UI gates the control with `canHardDeleteQuote` so the button only shows
 * where the rule would allow it; this is the raw write.
 */
export async function deleteQuote(quoteId: string): Promise<void> {
  await deleteDoc(doc(db, 'quotes', quoteId));
}

/**
 * SOFT path for a SENT quote the team wants to retire WITHOUT a hard delete —
 * the rule-allowed sent→superseded / sent→expired transition (no rules deploy
 * needed; the deployed UPDATE rule already permits it). Use this to declutter a
 * sent quote that you don't (or can't) hard-delete.
 */
export async function retireSentQuote(
  quoteId: string,
  to: 'superseded' | 'expired',
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'quotes', quoteId), {
    status: to,
    updatedBy: actorName,
    _updatedAt: serverTimestamp(),
  });
}

/**
 * SUPERSEDE-and-REVISE (2026-07-07 owner ask) — a TRUE supersede: void the old
 * quote AND open an editable copy, instead of the old "archive only" retire.
 *
 * TWO rule-conformant writes, ordered create-FIRST so a partial failure never
 * leaves a dangling `supersededBy`:
 *  1. CREATE the revised draft via the EXISTING `createQuote` path — the SAME
 *     `quoteCreateBody` the composer/reconcile use, so it satisfies the deployed
 *     CREATE rule (`status:'draft'`, a non-empty leadId OR projectId, NO
 *     forbidden CF fields). It copies every editable field of `old` and carries
 *     `relatedFrom = old.id` (audit lineage + the "Revised from" chip). The
 *     server stamps quoteNo/validUntil/total.
 *  2. VOID the old quote: the rule-allowed sent→superseded transition (admin-
 *     only for accepted→superseded — the caller is admin) PLUS `supersededBy`.
 *     ONLY the status flip + `supersededBy` are written — none of the
 *     CF-immutable fields (`quoteNo`/`partyId`/`validUntil`/`subtotal`/`total`/
 *     `paymentLinkUrl`) are touched, so the deployed UPDATE rule permits it.
 *
 * Returns the new draft's id so the caller can auto-open it in the editor.
 */
export async function supersedeAndReviseQuote(
  old: Quote,
  actorName: string,
): Promise<{ newId: string }> {
  const now = new Date().toISOString();
  const draft = reviseQuoteDraft(old, `QT-${Date.now()}`, now);
  // 1) the editable copy — reuses the create seam (rule-valid draft create).
  await createQuote(draft, actorName);
  // 2) void the old quote — status flip + supersededBy only (no CF fields).
  await updateDoc(doc(db, 'quotes', old.id), {
    status: 'superseded',
    supersededBy: draft.id,
    updatedBy: actorName,
    _updatedAt: serverTimestamp(),
  });
  return { newId: draft.id };
}

/* ════════════════════ Tracked-quotes for a lead / project ════════════════════ */

/** Live quotes raised from a given lead (for the lead detail's quote chip). */
export function listenQuotesForLead(
  leadId: string,
  cb: (rows: Quote[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'quotes'), where('leadId', '==', leadId)),
    (snap) => {
      const rows: Quote[] = [];
      for (const dd of snap.docs) {
        const data = dd.data();
        if (!Array.isArray(data.lines)) continue;
        rows.push(quoteFromDoc(dd.id, data));
      }
      cb(sortQuotes(rows));
    },
    (e) => onErr?.(e),
  );
}
